'use client'

import OfferLinkArrow from './offer-link-arrow'
import { SHOW_OFFER_RECOVERY } from '@/utils/offer-ui-flags'
import OfferUsd from './offer-usd'
import { useEffect, useRef, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { offerFundingReplaced, type Offer } from '@/utils/offers'
import {
	buildOfferRefund,
	cancelSentOffer,
	formatOfferBtc,
	RefundOutcomeUnknown
} from '@/utils/offer-inbox'
import { reconcileOffer } from '@/utils/offer-inbox'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import { EXPLORER_URL } from '@/utils/constants'
import OfferDialog from './offer-dialog'
import OfferTargetSummary from './offer-target-summary'
import styles from './offer-dialog.module.css'
import Link from 'next/link'
import { backupFromPsbt, saveRecoveryBackup } from '@/utils/offer-recovery'
import { ensureEccLib } from '@/utils/wallet'
import { beginOfferFlow, OFFER_STEP_STAGES } from '@/utils/offer-telemetry'

export default function CancelOfferModal({
	offer,
	onClose,
	onChanged
}: {
	offer: Offer
	onClose: () => void
	onChanged?: () => void
}) {
	const { data: rates, isLoading: feesLoading, isFallback: feesFallback } = useFeeEstimates()
	// Null until the buyer types a rate. The suggestion follows live estimates
	// only; the 1 sat/vB fallback is never used as a refund fee by default.
	const [typedFee, setTypedFee] = useState<string | null>(null)
	const suggestedFee = !feesLoading && !feesFallback ? String(Math.max(1, rates?.halfHourFee || 2)) : ''
	const fee = typedFee ?? suggestedFee
	const rate = Number(fee)
	const feePending = typedFee === null && feesLoading
	const feeMissing = typedFee === null && !feesLoading && feesFallback
	const {
		data: refund,
		error: loadError,
		isLoading,
		mutate: rebuild
	} = useSWR(fee ? ['offers:refund', offer.id, rate] : null, () => buildOfferRefund(offer, rate), {
		revalidateOnFocus: false,
		shouldRetryOnError: false
	})
	const [step, setStep] = useState<'checking' | 'signing' | 'submitting' | null>(null)
	const [error, setError] = useState<Error | null>(null)
	const [txid, setTxid] = useState<string | null>(null)
	const [confirmed, setConfirmed] = useState(false)
	const [saleWon, setSaleWon] = useState(false)
	// The funding was replaced before it confirmed: there is no escrow to refund.
	const [fundingReplaced, setFundingReplaced] = useState(() => offerFundingReplaced(offer))
	const operation = useRef<AbortController | null>(null)
	const { mutate } = useSWRConfig()
	useEffect(() => {
		if (!refund) return
		void ensureEccLib()
			.then(() => {
				saveRecoveryBackup(
					backupFromPsbt(
						refund.psbt,
						0,
						offer.id,
						offer.buyer_payment_address,
						offer.recovery_delay_blocks
					)
				)
			})
			.catch(() => {
				/* Existing offers may lack recovery metadata; do not invent a backup. */
			})
	}, [refund, offer.id, offer.buyer_payment_address, offer.recovery_delay_blocks])
	useEffect(() => () => operation.current?.abort(), [])
	const close = () => {
		operation.current?.abort()
		onClose()
	}
	const uncertain =
		error instanceof RefundOutcomeUnknown || loadError instanceof RefundOutcomeUnknown
	const { data: reconciled } = useSWR(
		!saleWon && !fundingReplaced && (uncertain || (txid && !confirmed)) ? ['offer:reconcile', offer.id] : null,
		() => reconcileOffer(offer.id),
		{
			refreshInterval: 5000,
			shouldRetryOnError: true,
			errorRetryInterval: 10000,
			errorRetryCount: 12
		}
	)
	useEffect(() => {
		if (reconciled?.state === 'accepted' && !saleWon) {
			setSaleWon(true)
			onChanged?.()
		}
		if (!confirmed && reconciled?.state === 'cancelled' && reconciled.cancel_txid) {
			setTxid(reconciled.cancel_txid)
			setConfirmed(true)
			setError(null)
			onChanged?.()
		}
		if (offerFundingReplaced(reconciled) && !fundingReplaced) {
			setFundingReplaced(true)
			setError(null)
			onChanged?.()
		}
	}, [reconciled, onChanged, confirmed, saleWon, fundingReplaced])
	const run = async () => {
		if (!refund || operation.current || uncertain || txid || fundingReplaced) return
		const controller = new AbortController()
		operation.current = controller
		setError(null)
		const telemetry = beginOfferFlow('offer_cancel', 'offer_dialog', {
			scope: offer.scope,
			collectionSlug: offer.collection_slug,
			priceSats: offer.price_sats,
			feeRate: rate,
			offerId: offer.id,
			role: 'buyer'
		})
		try {
			const result = await cancelSentOffer(
				offer,
				refund,
				(next) => {
					if (!controller.signal.aborted) setStep(next)
					if (OFFER_STEP_STAGES[next]) telemetry.stage(OFFER_STEP_STAGES[next])
				},
				controller.signal
			)
			telemetry.success()
			void mutate(
				(key) =>
					Array.isArray(key) && String(key[0]).startsWith('offers:') && key[0] !== 'offers:refund'
			)
			onChanged?.()
			if (!controller.signal.aborted) {
				setTxid(result.txid)
				setConfirmed(!result.pending)
			}
		} catch (e) {
			telemetry.failure(e)
			if (!controller.signal.aborted)
				setError(e instanceof Error ? e : new Error('Could not cancel this offer.'))
		} finally {
			operation.current = null
			if (!controller.signal.aborted) setStep(null)
		}
	}
	return (
		<OfferDialog
			title={
				saleWon
					? 'Offer accepted'
					: fundingReplaced
					? 'Funding replaced'
					: confirmed
					? 'Offer cancelled'
					: txid
					? 'Refund submitted'
					: 'Cancel Offer'
			}
			description={
				saleWon
					? 'The purchase confirmed before the refund. Your offer funds paid for the item.'
					: fundingReplaced
					? 'This offer’s funding was replaced before it confirmed, so no BTC was escrowed and there is nothing to refund.'
					: 'Return the escrowed BTC to your original payment wallet. A network fee is deducted from the refund.'
			}
			onClose={close}
			footer={
				<>
					{!txid && !uncertain && !fundingReplaced && (
						<button type="button" className={styles.cancel} onClick={close}>
							{step === 'submitting' ? 'Close' : 'Keep offer'}
						</button>
					)}
					<button
						type="button"
						className={styles.submit}
						disabled={
							!txid && !uncertain && !fundingReplaced && (!!step || !refund || !!loadError || isLoading)
						}
						onClick={txid || uncertain || fundingReplaced ? close : run}
					>
						{txid || uncertain || fundingReplaced
							? 'Done'
							: step === 'submitting'
							? 'Submitting refund…'
							: step
							? 'Waiting for wallet…'
							: 'Cancel offer & refund'}
					</button>
				</>
			}
		>
			<OfferTargetSummary offer={offer} />
			{SHOW_OFFER_RECOVERY && (
				<p className={styles.hint}>
					<Link href="/offers/recovery">
						Recovery backup & buyer-only recovery <OfferLinkArrow />
					</Link>
				</p>
			)}
			{!txid && !fundingReplaced && (
				<label className={styles.fieldLabel}>
					Network fee · sat/vB
					<input
						className={styles.refundFee}
						aria-label="Refund network fee"
						type="number"
						min="1"
						max="500"
						step="1"
						value={fee}
						placeholder={feePending ? 'Loading…' : undefined}
						disabled={!!step || uncertain}
						onChange={(e) => {
							setTypedFee(e.target.value)
							setError(null)
						}}
					/>
				</label>
			)}
			{!txid && !fundingReplaced && feePending && (
				<p role="status" className={styles.hint}>
					Loading fee estimates…
				</p>
			)}
			{!txid && !fundingReplaced && feeMissing && (
				<p role="alert" className={styles.error} data-testid="refund-fee-unavailable">
					Fee estimates are unavailable right now. Enter a custom fee rate to continue.
				</p>
			)}
			{saleWon && (
				<p role="status">
					This offer was accepted before cancellation completed. The escrow paid for the purchase;
					no refund is available.
				</p>
			)}
			{refund && !saleWon && !fundingReplaced && (
				<div className={styles.proceeds}>
					<span>{confirmed ? 'Refunded' : 'Refund amount'}</span>
					<strong>
						{formatOfferBtc(refund.refundSats)} BTC <OfferUsd sats={refund.refundSats} />
					</strong>
					<small>
						Network fee: {formatOfferBtc(refund.feeSats)} BTC <OfferUsd sats={refund.feeSats} />
					</small>
				</div>
			)}
			{isLoading && <p role="status">Preparing your refund…</p>}
			{!txid && (error || loadError) && (
				<p role="alert" className={styles.error}>
					{(error || loadError).message}
				</p>
			)}
			{!txid && loadError && !uncertain && (
				<button className={styles.cancel} onClick={() => void rebuild()}>
					Retry
				</button>
			)}
			{step && (
				<p role="status" className={styles.hint}>
					{step === 'submitting'
						? 'The refund is being submitted. Closing won’t stop it.'
						: 'You can close this review to stop before submission.'}
				</p>
			)}
			{txid && !saleWon && (
				<p role="status">
					{confirmed
						? 'Refund confirmed.'
						: 'Refund submitted. Waiting for confirmation; an earlier sale may still confirm first.'}{' '}
					<a
						href={`${EXPLORER_URL}/tx/${txid}`}
						target="_blank"
						rel="noreferrer"
						style={{ whiteSpace: 'nowrap' }}
					>
						View transaction <OfferLinkArrow />
					</a>
				</p>
			)}
		</OfferDialog>
	)
}
